import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { FormFeedback, Label } from 'reactstrap';

import { Button, IconButton } from '../../../../../../_shared';
import { messages } from '../../../../../../_shared/i18n/messages';

import { FIELD_TYPES } from '../../../../../constants';
import { useFormEditorContext } from '../../../contexts/FormEditorContext';
import {
	FORM_EDITOR_FIELDS,
	OPTION_EDITOR_FIELDS,
} from '../../../hooks/useFormEditorData';
import {
	formFieldOptionDataShape,
	fieldOptionInputValidationShape,
} from '../../../utils/propTypes';

import FormEditorDummyInput from './FormEditorDummyInput';
import FormNavigationOptionsDropdown, { FORM_ITEM_TYPE_OPTION }
	from './FormNavigationOptionsDropdown';

const propTypes = {

	/**
	 * The id of the field.
	 */
	fieldId: PropTypes.string.isRequired,

	/**
	 * The field type.
	 */
	type: PropTypes.oneOf([
		FIELD_TYPES.CHECKBOXES,
		FIELD_TYPES.RADIO,
		FIELD_TYPES.SELECT,
	]).isRequired,

	/**
	 * The form field option.
	 */
	option: formFieldOptionDataShape,

	/**
	 * The input validation for the form field option.
	 */
	optionValidation: fieldOptionInputValidationShape,

	/**
	 * The index of the form field option relative to the list of options.
	 */
	index: PropTypes.number.isRequired,

	/**
	 * Flag indicating if the form field option is a placeholder option.
	 */
	placeholder: PropTypes.bool.isRequired,

	/**
	 * True, if allowed to delete this form field option.
	 */
	showDeleteButton: PropTypes.bool.isRequired,

	/**
	 * Flag indicating if conditional navigation options for the form field
	 * option should be visible.
	 */
	showNavOptions: PropTypes.bool.isRequired,

	/**
	 * Function to choose a drag source, injected by react-dnd.
	 */
	connectDragSource: PropTypes.func,

};

const defaultProps = {
	placeholder: false,
	showDeleteButton: false,
	showNavOptions: false,
};

const FormEditorFieldOption = (props) => {

	const {
		fieldId,
		type,
		option,
		optionValidation,
		index,
		placeholder,
		showDeleteButton,
		showNavOptions,
		connectDragSource
	} = props;

	const intl = useIntl();

	const {
		formData,
		isEditingDefaultLanguage,
		defaultLanguage,
		selectedLanguage,
		actions,
	} = useFormEditorContext();

	const onChange = useCallback((optionID, name, value) => {

		return actions.options.update(fieldId, optionID, name, value);

	}, [fieldId, actions.options]);

	const optionCss = 'tc-form-editor-field-option';

	// Check if this is a placeholder option
	if (placeholder) {

		if (!isEditingDefaultLanguage) {
			return null;
		}

		return (
			<div className={optionCss}>
				<div className="tc-form-editor-field-option-action-dnd" />
				<FormEditorDummyInput
					fieldId={fieldId}
					type={type}
					option={{ id: 'placeholder-option' }}
					index={index} />
				{' '}
				<Button
					className="tc-form-editor-field-placeholder-option"
					color="secondary"
					onClick={() => actions.options.add(fieldId)}>
					{intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_HOVER_TEXT_ADD_OPTION)}
				</Button>
			</div>
		);

	}

	// Get the form field option content data for the selected language
	const activeTranslation =
		option[OPTION_EDITOR_FIELDS.CONTENTS].find(
			(content) => content.language === selectedLanguage
		);

	// Get the form field option content data for the default language
	const defaultTranslation =
		option[OPTION_EDITOR_FIELDS.CONTENTS].find(
			(content) => content.language === defaultLanguage
		);

	// Get the validation state for the form field option content data for the selected language
	const validationState =
		optionValidation?.[OPTION_EDITOR_FIELDS.CONTENTS]?.data?.[selectedLanguage];

	// Check if the form field option is in an invalid state
	const isOptionInvalid =
		validationState?.[OPTION_EDITOR_FIELDS.LABEL]?.invalid;

	const placeholderTextForOptionValue =
		isEditingDefaultLanguage
			? intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_MSG_TEXT_FIELD_OPTION_NAME, {
				name: index + 1,
			}) : defaultTranslation?.[OPTION_EDITOR_FIELDS.LABEL];

	return (
		<div className={optionCss}>
			{isEditingDefaultLanguage &&
				<div className="tc-form-editor-field-option-action-dnd">
					{connectDragSource && connectDragSource(
						<div
							className="tc-form-editor-field-option-action drag-source-handle"
							title={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_HOVER_TEXT_MOVE_OPTION)}>
							<i className="fas fa-grip-vertical" />
						</div>
					)}
				</div>
			}
			<Label
				for={`${option.id}-value`}
				hidden>
				{placeholderTextForOptionValue}
			</Label>
			<FormEditorDummyInput
				fieldId={fieldId}
				type={type}
				option={option}
				index={index} />
			{' '}
			<input
				className={`
					tc-material-input
					${isOptionInvalid ? 'tc-material-input-invalid' : ''}
				`}
				aria-describedby={
					isOptionInvalid ? `${option.id}-label-and-value-feedback` : undefined
				}
				name={`${option.id}-label-and-value`}
				type="text"
				value={activeTranslation?.[OPTION_EDITOR_FIELDS.LABEL] || ''}
				placeholder={placeholderTextForOptionValue}
				onChange={(event) => {

					const newValue = event.target.value;

					onChange(option.id, OPTION_EDITOR_FIELDS.LABEL, newValue);

					// Keep the option label and value in sync for the default language
					if (isEditingDefaultLanguage) {
						onChange(option.id, OPTION_EDITOR_FIELDS.VALUE, newValue);
					}

				}} />
			{isOptionInvalid &&
				<FormFeedback
					id={`${option.id}-label-and-value-feedback`}
					aria-live="assertive">
					{validationState?.[OPTION_EDITOR_FIELDS.LABEL]?.error}
				</FormFeedback>
			}
			{isEditingDefaultLanguage &&
				<>
					{showDeleteButton &&
						<IconButton
							className="tc-form-editor-field-option-action"
							title={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_HOVER_TEXT_REMOVE_OPTION)}
							icon="far fa-xmark fa-lg"
							size="30px"
							onClick={() => actions.options.delete(fieldId, option.id)} />
					}
					{showNavOptions &&
						<FormNavigationOptionsDropdown
							formItem={option}
							formItemType={FORM_ITEM_TYPE_OPTION}
							sections={formData[FORM_EDITOR_FIELDS.SECTIONS]}
							onChange={onChange} />
					}
				</>
			}
		</div>
	);

};

FormEditorFieldOption.propTypes = propTypes;
FormEditorFieldOption.defaultProps = defaultProps;

export default FormEditorFieldOption;
